import React, { useMemo, useState } from "react";
import HistoricoCadastro from "../HistoricoCadastro/HistoricoCadastro";
import "./listausuarios.css";

const SUPERADMIN_EMAIL = "robertopaiva25@hotmail.com";

function normalizarEmail(email) {
  return String(email || "").trim().toLowerCase();
}

export function labelTipoUsuario(usuario) {
  if (normalizarEmail(usuario.email) === SUPERADMIN_EMAIL) {
    return "Superadmin";
  }

  if (usuario.plano === "pessoal" || usuario.perfilSaas === "adminsaasPessoal") return "Admin SaaS Pessoal";
  if (usuario.tipo === "admin" && usuario.adminPrincipal) return "Admin principal SaaS";
  if (usuario.tipo === "admin") return "Administrador auxiliar";
  if (usuario.tipo === "cliente") return "Cliente";
  if (usuario.tipo === "colaborador") return "Colaborador";

  if (usuario.tipo === "dp") return "Departamento Pessoal";


  return usuario.tipo || "-";
}

function dataPlano(usuario) {
  const valor = usuario.assinaturaExpiraEm || usuario.demonstracaoExpiraEm;
  if (!valor) return "";
  const data = valor.toDate ? valor.toDate() : new Date(valor.seconds ? valor.seconds * 1000 : valor);
  return Number.isNaN(data.getTime()) ? "" : data.toLocaleDateString("pt-BR");
}

export function planoUsuario(usuario) {
  if (normalizarEmail(usuario.email) === SUPERADMIN_EMAIL) return { label: "-", classe: "" };
  if (usuario.plano === "pessoal") return { label: "Pessoal", classe: "plano-pessoal" };
  if (usuario.planoSaas) {
    const nomePlano = String(usuario.planoSaas).charAt(0).toUpperCase() + String(usuario.planoSaas).slice(1);
    const ciclo = usuario.cicloCobranca || usuario.plano;
    const nomeCiclo = ["mensal", "anual"].includes(ciclo)
      ? String(ciclo).charAt(0).toUpperCase() + String(ciclo).slice(1)
      : "";
    return {
      label: nomeCiclo ? `${nomePlano} / ${nomeCiclo}` : nomePlano,
      classe: usuario.planoSaas === "pessoal" ? "plano-pessoal" : "plano-mensal",
    };
  }
  if (usuario.plano === "demonstracao" || usuario.demonstracaoExpiraEm) {
    return { label: "Demonstração", classe: "plano-demonstracao" };
  }
  return { label: "Mensal", classe: "plano-mensal" };
}

function ListaUsuarios({
  usuarios = [],
  alterarStatus,
  editarUsuario,
  gerenciarTenant,
  gerenciarPlano,
  mostrarTenant = false,
}) {
  const [ordenacao, setOrdenacao] = useState({ coluna: "nome", direcao: "asc" });
  const [tenantsExpandidos, setTenantsExpandidos] = useState(() => new Set());

  function alternarTenant(tenantId) {
    setTenantsExpandidos((atuais) => {
      const proximos = new Set(atuais);
      if (proximos.has(tenantId)) proximos.delete(tenantId);
      else proximos.add(tenantId);
      return proximos;
    });
  }

  function alternarOrdenacao(coluna) {
    setOrdenacao((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));
  }

  const usuariosOrdenados = useMemo(() => {
    const valorColuna = (usuario) => {
      if (ordenacao.coluna === "tipo") return labelTipoUsuario(usuario);
      if (ordenacao.coluna === "tenant") return usuario.tenantNome || usuario.tenantId || "";
      if (ordenacao.coluna === "plano") return planoUsuario(usuario).label;
      return usuario[ordenacao.coluna] || "";
    };

    return [...usuarios].sort((usuarioA, usuarioB) => {
      const resultado = String(valorColuna(usuarioA)).localeCompare(
        String(valorColuna(usuarioB)),
        "pt-BR",
        { sensitivity: "base", numeric: true }
      );
      return ordenacao.direcao === "asc" ? resultado : -resultado;
    });
  }, [usuarios, ordenacao]);

  const usuariosVisiveis = useMemo(() => {
    if (!mostrarTenant) {
      return usuariosOrdenados.map((usuario) => ({ usuario, subitem: false }));
    }

    const linhas = [];
    usuariosOrdenados.forEach((usuario) => {
      const superadmin = normalizarEmail(usuario.email) === SUPERADMIN_EMAIL;
      if (!superadmin && !usuario.adminPrincipal) return;

      linhas.push({ usuario, subitem: false });
      if (!usuario.adminPrincipal || !tenantsExpandidos.has(usuario.tenantId)) return;

      usuariosOrdenados
        .filter((subitem) =>
          subitem.id !== usuario.id &&
          subitem.tenantId &&
          subitem.tenantId === usuario.tenantId
        )
        .forEach((subitem) => linhas.push({ usuario: subitem, subitem: true }));
    });
    return linhas;
  }, [mostrarTenant, tenantsExpandidos, usuariosOrdenados]);

  function cabecalhoOrdenavel(titulo, coluna) {
    const ativo = ordenacao.coluna === coluna;
    return (
      <th
        className="lista-cabecalho-ordenavel"
        onClick={() => alternarOrdenacao(coluna)}
        title={`Ordenar por ${titulo}`}
      >
        {titulo}
        <span aria-hidden="true">{ativo ? (ordenacao.direcao === "asc" ? " ▲" : " ▼") : " ↕"}</span>
      </th>
    );
  }

  return (
    <div className="cadastro-tabela">
      <table className="table table-hover table-striped table-bordered mb-0 lista-cadastro-padrao">
        <thead>
          <tr>
            {cabecalhoOrdenavel("Nome", "nome")}
            {cabecalhoOrdenavel("E-mail", "email")}
            {cabecalhoOrdenavel("Tipo", "tipo")}
            {mostrarTenant && cabecalhoOrdenavel("Cliente SaaS / Empresa", "tenant")}
            {mostrarTenant && cabecalhoOrdenavel("Plano", "plano")}
            {cabecalhoOrdenavel("Status", "status")}
          </tr>
        </thead>

        <tbody>
          {usuarios.length === 0 && (
            <tr>
              <td colSpan={mostrarTenant ? 6 : 4} className="text-center texto-secundario">
                Nenhum usuario encontrado.
              </td>
            </tr>
          )}

          {usuariosVisiveis.map(({ usuario, subitem }) => (
            <tr key={usuario.id} className={subitem ? "lista-usuario-subitem" : ""}>
              <td className="lista-dado-principal">
                {mostrarTenant && usuario.adminPrincipal ? (
                  <button
                    type="button"
                    className="btn btn-sm lista-botao-expandir"
                    onClick={() => alternarTenant(usuario.tenantId)}
                    aria-expanded={tenantsExpandidos.has(usuario.tenantId)}
                    title="Mostrar usuarios desta empresa"
                  >
                    <span aria-hidden="true">
                      {tenantsExpandidos.has(usuario.tenantId) ? "-" : "+"}
                    </span>
                    {usuario.nome}
                  </button>
                ) : (
                  <span className={subitem ? "lista-subitem-nome" : ""}>{usuario.nome}</span>
                )}
              </td>
              <td className="lista-dado-secundario">{usuario.email}</td>
              <td className="lista-dado-destaque">{labelTipoUsuario(usuario)}</td>
              {mostrarTenant && (
                <td>{usuario.tenantNome || usuario.tenantId || "-"}</td>
              )}
              {mostrarTenant && (
                <td>
                  <span className={`plano-badge ${planoUsuario(usuario).classe}`}>
                    {planoUsuario(usuario).label}
                  </span>
                  {dataPlano(usuario) && <small className="plano-vencimento">até {dataPlano(usuario)}</small>}
                </td>
              )}

              <td style={{ width: 350 }}>
                <div className="d-flex align-items-center gap-2">
                  <select
                    className="form-select"
                    value={usuario.status || "ativo"}
                    onChange={(event) => alterarStatus(usuario.id, event.target.value)}
                    disabled={usuario.adminPrincipal && !mostrarTenant}
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>

                  {mostrarTenant && usuario.adminPrincipal && normalizarEmail(usuario.email) !== SUPERADMIN_EMAIL && (
                    <>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => gerenciarPlano(usuario)}
                      >
                        Plano
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => gerenciarTenant(usuario)}
                      >
                        Gerenciar
                      </button>
                    </>
                  )}

                  {!mostrarTenant && !usuario.adminPrincipal && ["cliente", "admin", "colaborador"].includes(usuario.tipo) && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => editarUsuario(usuario)}
                    >
                      Editar
                    </button>
                  )}
                  <HistoricoCadastro colecao="users" documentoId={usuario.id} registro={usuario} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ListaUsuarios;
